import React, { useState, useEffect } from 'react';
import {
  Dialog,
  Flex,
  Text,
  TextField,
  Button,
  Select,
} from '@radix-ui/themes';
import { useCreateWebhookMutation } from '../../graphql/mutations/CreateWebhook.generated';
import { useUpdateWebhookMutation } from '../../graphql/mutations/UpdateWebhook.generated';

interface WebhookDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  webhook?: {
    id: string;
    label: string;
    url: string;
    method: string;
    type: string;
    ownerUserId?: string | null;
    ownerTeamId?: string | null;
  } | null;
  onSuccess?: () => void;
}

export default function WebhookDialog({
  open,
  onOpenChange,
  webhook,
  onSuccess,
}: WebhookDialogProps) {
  const [label, setLabel] = useState('');
  const [url, setUrl] = useState('');
  const [method, setMethod] = useState('POST');
  const [type, setType] = useState('HTTP');
  const [error, setError] = useState<string | null>(null);

  const [createWebhook, { loading: creating }] = useCreateWebhookMutation();
  const [updateWebhook, { loading: updating }] = useUpdateWebhookMutation();

  const isEdit = !!webhook;
  const loading = creating || updating;

  useEffect(() => {
    if (webhook) {
      setLabel(webhook.label);
      setUrl(webhook.url);
      setMethod(webhook.method);
      setType(webhook.type);
    } else {
      setLabel('');
      setUrl('');
      setMethod('POST');
      setType('HTTP');
    }
    setError(null);
  }, [webhook, open]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!label.trim()) {
      setError('Label is required');
      return;
    }

    if (!url.trim()) {
      setError('URL is required');
      return;
    }

    // Basic URL validation
    try {
      new URL(url);
    } catch {
      setError('Invalid URL format');
      return;
    }

    try {
      if (isEdit) {
        await updateWebhook({
          variables: {
            id: webhook.id,
            input: {
              label: label.trim(),
              url: url.trim(),
              method,
            },
          },
          refetchQueries: ['GetWebhooks'],
        });
      } else {
        await createWebhook({
          variables: {
            input: {
              label: label.trim(),
              url: url.trim(),
              method,
              type,
            },
          },
          refetchQueries: ['GetWebhooks'],
        });
      }

      onOpenChange(false);
      setLabel('');
      setUrl('');
      setMethod('POST');
      setType('HTTP');
      onSuccess?.();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
    }
  };

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Content style={{ maxWidth: 500 }}>
        <Dialog.Title>{isEdit ? 'Edit Webhook' : 'Add Webhook'}</Dialog.Title>
        <Dialog.Description size="2" mb="4">
          {isEdit
            ? 'Update the webhook configuration.'
            : 'Add a new webhook integration.'}
        </Dialog.Description>

        <form onSubmit={handleSubmit}>
          <Flex direction="column" gap="4">
            <label>
              <Text as="div" size="2" mb="1" weight="bold">
                Label
              </Text>
              <TextField.Root
                placeholder="My Webhook"
                value={label}
                onChange={(e) => setLabel(e.target.value)}
                disabled={loading}
              />
            </label>

            <label>
              <Text as="div" size="2" mb="1" weight="bold">
                Type
              </Text>
              <Select.Root
                value={type}
                onValueChange={setType}
                disabled={loading || isEdit}
              >
                <Select.Trigger style={{ width: '100%' }} />
                <Select.Content>
                  <Select.Item value="HTTP">HTTP</Select.Item>
                  <Select.Item value="SLACK">Slack</Select.Item>
                  <Select.Item value="DISCORD">Discord</Select.Item>
                </Select.Content>
              </Select.Root>
            </label>

            <label>
              <Text as="div" size="2" mb="1" weight="bold">
                URL
              </Text>
              <TextField.Root
                placeholder="https://example.com/webhook"
                value={url}
                onChange={(e) => setUrl(e.target.value)}
                disabled={loading}
              />
            </label>

            <label>
              <Text as="div" size="2" mb="1" weight="bold">
                Method
              </Text>
              <Select.Root
                value={method}
                onValueChange={setMethod}
                disabled={loading}
              >
                <Select.Trigger style={{ width: '100%' }} />
                <Select.Content>
                  <Select.Item value="GET">GET</Select.Item>
                  <Select.Item value="POST">POST</Select.Item>
                  <Select.Item value="PUT">PUT</Select.Item>
                  <Select.Item value="PATCH">PATCH</Select.Item>
                  <Select.Item value="DELETE">DELETE</Select.Item>
                </Select.Content>
              </Select.Root>
            </label>

            {error && (
              <Text color="red" size="2">
                {error}
              </Text>
            )}

            <Flex gap="3" mt="4" justify="end">
              <Dialog.Close>
                <Button variant="soft" color="gray" disabled={loading}>
                  Cancel
                </Button>
              </Dialog.Close>
              <Button type="submit" disabled={loading}>
                {loading ? 'Saving...' : isEdit ? 'Update' : 'Create'}
              </Button>
            </Flex>
          </Flex>
        </form>
      </Dialog.Content>
    </Dialog.Root>
  );
}
